import React, { memo, useEffect } from 'react';
import { usePresence } from '../../hooks/usePresence';
import { useAuth } from '../../hooks/useAuth';

/**
 * Memoized avatar component with real-time online status indicator.
 * Extracted from UnifiedMessagingPage to prevent remount on every parent render.
 */
const ConversationAvatar = memo(({ profilePicture, name, userId, size = 'w-12 h-12', textSize = 'font-semibold', showOnlineStatus = false }) => {
  const { getUserPresence, isUserOnline, fetchUserPresence } = usePresence();
  const { user } = useAuth();
  const initial = name?.charAt(0)?.toUpperCase() || '?';

  // Get real-time presence data for this user (synchronous read from context state)
  const presenceData = userId ? getUserPresence(userId) : null;

  // Fetch presence data when component mounts or userId changes
  useEffect(() => {
    if (userId && showOnlineStatus && user?.id) {
      fetchUserPresence(userId).catch(() => {
        // Silent — presence fetch failures are non-critical
      });
    }
  }, [userId, showOnlineStatus, fetchUserPresence, user?.id]);

  const getOnlineIndicatorSize = () => {
    if (size.includes('w-10') || size.includes('h-10')) return 'w-3 h-3';
    if (size.includes('w-8') || size.includes('h-8')) return 'w-2.5 h-2.5';
    return 'w-3.5 h-3.5';
  };

  const getOnlineIndicatorPosition = () => {
    if (size.includes('w-10') || size.includes('h-10')) return 'bottom-0 right-0';
    if (size.includes('w-8') || size.includes('h-8')) return 'bottom-0 right-0';
    return 'bottom-0.5 right-0.5';
  };

  const getIndicatorColor = () => {
    if (!presenceData) return 'bg-gray-400';
    switch (presenceData.status) {
      case 'online':
        return 'bg-green-500';
      case 'away':
      case 'idle':
        return 'bg-yellow-500';
      case 'busy':
        return 'bg-red-500';
      case 'offline':
      default:
        return 'bg-gray-400';
    }
  };

  const OnlineIndicator = showOnlineStatus ? (
    <div className="relative">
      <div
        className={`absolute ${getOnlineIndicatorPosition()} ${getOnlineIndicatorSize()} ${getIndicatorColor()} border-2 border-white rounded-full`}
        title={presenceData ? `${presenceData.status} ${presenceData.last_activity ? `- Last active: ${new Date(presenceData.last_activity).toLocaleString()}` : ''}` : 'Status unknown'}
      />
      {presenceData?.status === 'online' && (
        <div
          className={`absolute ${getOnlineIndicatorPosition()} ${getOnlineIndicatorSize()} bg-green-500 rounded-full opacity-75`}
        />
      )}
    </div>
  ) : null;

  if (profilePicture) {
    return (
      <div className="relative">
        <img
          src={profilePicture}
          alt={`${name}'s profile`}
          className={`${size} rounded-full object-cover flex-shrink-0 border-2 border-white shadow-sm`}
        />
        {OnlineIndicator}
      </div>
    );
  }

  return (
    <div className="relative">
      <div className={`${size} bg-blue-500 rounded-full flex items-center justify-center text-white ${textSize} flex-shrink-0`}>
        {initial}
      </div>
      {OnlineIndicator}
    </div>
  );
});

ConversationAvatar.displayName = 'ConversationAvatar';
export default ConversationAvatar;
